/**
 * division-registry — pure enum-migration / division-resolution logic
 * (division-registry spec, task 2.1).
 *
 * The strangler-fig migration keeps the `ApplicantType` enum working while a
 * nullable `divisionKey` reference is introduced on `ApplicantYearProfile`
 * (design §Architecture → Enum Migration Strategy). This module is the single
 * canonical answer to "what Division is this profile?":
 *
 *   resolveDivisionKey(profile) = profile.divisionKey ?? backCompatMapping[applicantType]
 *
 * It is PURE — no I/O, no AWS SDK — so it can be exercised by `vitest`
 * (frontend) and `jest` (Lambda) against the same code and reused by the merit
 * derivation + back-fill.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.8
 */

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * The hardcoded GraphQL enum `ApplicantType { MRS | MISS }` that this feature
 * migrates away from (Glossary: Applicant_Type_Enum).
 */
export type ApplicantType = 'MRS' | 'MISS';

/**
 * Minimal Division shape needed for resolution.
 *
 * NOTE: the canonical `Division` interface is owned by the sibling
 * `./registryLogic` module (task 1.1). This local definition is structurally
 * compatible so that, once `registryLogic` exports `Division`, callers can pass
 * either without change; the import can be switched to
 * `import type { Division } from './registryLogic'` in a later step.
 */
export interface Division {
  id: string;
  competitionYear: number;
  key: string;
  label: string;
  displayOrder: number;
  appliesToApplications: boolean;
  appliesToJudging: boolean;
  active: boolean;
}

/**
 * The subset of `ApplicantYearProfile` this module reads. `divisionKey` is the
 * new nullable strangler reference; `applicantType` is the legacy enum that
 * still resolves every existing row through the Back-Compat mapping.
 */
export interface ProfileLike {
  /** Identifies the profile in error messages (Req 3.8). */
  applicantId: string;
  /** New nullable strangler reference; takes precedence when set. */
  divisionKey?: string | null;
  /** Legacy enum, mapped through the Back-Compat mapping as a fallback. */
  applicantType: ApplicantType;
}

// -----------------------------------------------------------------------------
// Back-Compat mapping (Req 3.1)
// -----------------------------------------------------------------------------

/**
 * Total mapping from the `ApplicantType` enum to its Back-Compat Division key.
 * The keys are the lowercased enum values, matching the `mrs`/`miss` Divisions
 * the migration seeds into the registry (design §Enum Migration Strategy step 1).
 */
export const backCompatMapping: Readonly<Record<ApplicantType, string>> = {
  MRS: 'mrs',
  MISS: 'miss',
};

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------

/**
 * Thrown when a profile's resolved Division key is absent from the year's
 * registry. Surfaces a descriptive error naming the missing key + profile
 * rather than silently dropping the contestant (Req 3.8).
 */
export class UnknownDivisionError extends Error {
  constructor(
    public readonly divisionKey: string,
    public readonly applicantId: string,
  ) {
    super(
      `Unknown division "${divisionKey}" referenced by ApplicantYearProfile ` +
        `(applicantId: "${applicantId}"): no matching Division exists in the ` +
        `registry for this competition year.`,
    );
    this.name = 'UnknownDivisionError';
  }
}

// -----------------------------------------------------------------------------
// Resolution
// -----------------------------------------------------------------------------

/**
 * The canonical Division key for a profile: use the explicit `divisionKey` when
 * set, otherwise map the legacy `applicantType` enum through the Back-Compat
 * mapping. Holds during and after the migration (Req 3.2, 3.3, 3.4).
 *
 * An empty or whitespace-only `divisionKey` is treated as unset so a blank value
 * never shadows the enum fallback.
 */
export function resolveDivisionKey(profile: ProfileLike): string {
  const explicit = profile.divisionKey;
  if (explicit != null && explicit.trim() !== '') {
    return explicit;
  }
  return backCompatMapping[profile.applicantType];
}

/**
 * Resolve a profile to its Division using the registry's divisions keyed by
 * Division key. Returns the Division, or throws {@link UnknownDivisionError}
 * naming the missing key + profile when absent (Req 3.8) — never returns
 * undefined silently.
 */
export function resolveDivision(
  profile: ProfileLike,
  divisionsByKey: ReadonlyMap<string, Division>,
): Division {
  const key = resolveDivisionKey(profile);
  const division = divisionsByKey.get(key);
  if (division === undefined) {
    throw new UnknownDivisionError(key, profile.applicantId);
  }
  return division;
}

// -----------------------------------------------------------------------------
// Projection — judging-disabled exclusion + displayOrder ordering (task 4.1)
// -----------------------------------------------------------------------------

/**
 * A profile that survived the judging exclusion filter, paired with the Division
 * it resolved to. The caller (merit derivation) reads `division.label` /
 * `division.key` and orders by `division.displayOrder`.
 */
export interface ProjectedContestant<P extends ProfileLike = ProfileLike> {
  /** The originating approved profile. */
  profile: P;
  /** The Division this profile resolved to (always judging-enabled + active). */
  division: Division;
}

/** Why a profile was excluded from the merit projection. */
export type ExclusionReason = 'judging-disabled' | 'inactive';

/**
 * A profile that was excluded from the merit projection because its resolved
 * Division is not judgeable. Surfaced as an indicator so the contestant is
 * never silently dropped (Req 2.7).
 */
export interface ExcludedContestant<P extends ProfileLike = ProfileLike> {
  /** The originating profile that was excluded. */
  profile: P;
  /** The Division the profile resolved to. */
  division: Division;
  /** Whether the Division was judging-disabled, inactive, or both. */
  reason: ExclusionReason;
}

/**
 * The result of {@link projectContestants}: the surviving, ordered contestants
 * plus a non-silent record of every exclusion (count + list) so the merit
 * surfaces can show an indicator rather than dropping contestants (Req 2.7).
 */
export interface ProjectContestantsResult<P extends ProfileLike = ProfileLike> {
  /**
   * Survivors ordered by Division `displayOrder` ascending, then by the
   * within-division order (the supplied comparator, else stable input order).
   */
  contestants: ProjectedContestant<P>[];
  /** The number of profiles excluded — the indicator (Req 2.7). */
  excludedCount: number;
  /** The excluded profiles with their reason — never a silent drop (Req 2.7). */
  excluded: ExcludedContestant<P>[];
}

/**
 * Options for {@link projectContestants}.
 */
export interface ProjectContestantsOptions<P extends ProfileLike = ProfileLike> {
  /**
   * Optional within-division ordering. A standard comparator (negative if `a`
   * sorts before `b`). When omitted, survivors keep their original relative
   * order within a Division (a stable sort), so callers that already sorted by
   * `appearanceOrder`/name upstream are preserved. Must be pure + deterministic.
   */
  withinDivision?: (a: P, b: P) => number;
}

/**
 * Project a set of approved profiles to their judgeable, ordered Merit
 * contestants (design §Components → Merit derivation changes; Properties 11/12):
 *
 *   1. Resolve each profile's Division via {@link resolveDivision}. An unknown
 *      Division key propagates {@link UnknownDivisionError} — never swallowed
 *      (Req 3.8).
 *   2. Exclude any profile whose resolved Division has `appliesToJudging` false
 *      OR `active` false, recording it (with a reason) in `excluded` and in
 *      `excludedCount` so the caller can surface an indicator (Req 2.7).
 *   3. Order survivors by Division `displayOrder` ascending, then by the
 *      within-division order (`opts.withinDivision`, else stable input order)
 *      (Req 2.8, 5.6).
 *
 * Pure: no I/O, no mutation of inputs, deterministic for fixed inputs.
 */
export function projectContestants<P extends ProfileLike>(
  profiles: readonly P[],
  divisionsByKey: ReadonlyMap<string, Division>,
  opts: ProjectContestantsOptions<P> = {},
): ProjectContestantsResult<P> {
  const survivors: { item: ProjectedContestant<P>; index: number }[] = [];
  const excluded: ExcludedContestant<P>[] = [];

  profiles.forEach((profile, index) => {
    // Propagates UnknownDivisionError for an absent key (Req 3.8).
    const division = resolveDivision(profile, divisionsByKey);

    const judgingDisabled = division.appliesToJudging === false;
    const inactive = division.active === false;
    if (judgingDisabled || inactive) {
      const reason: ExclusionReason = judgingDisabled
        ? 'judging-disabled'
        : 'inactive';
      excluded.push({ profile, division, reason });
      return;
    }

    survivors.push({ item: { profile, division }, index });
  });

  // Order by Division displayOrder asc, then within-division order. The original
  // index is the stable tiebreaker so the sort is deterministic and preserves
  // the caller's upstream within-division order when no comparator is given.
  const withinDivision = opts.withinDivision;
  survivors.sort((a, b) => {
    const byDisplayOrder =
      a.item.division.displayOrder - b.item.division.displayOrder;
    if (byDisplayOrder !== 0) {
      return byDisplayOrder;
    }
    if (withinDivision !== undefined) {
      const byWithin = withinDivision(a.item.profile, b.item.profile);
      if (byWithin !== 0) {
        return byWithin;
      }
    }
    return a.index - b.index;
  });

  return {
    contestants: survivors.map((s) => s.item),
    excludedCount: excluded.length,
    excluded,
  };
}
